import React, { useState, useEffect } from 'react';
import './AddVendor.css';
import { API_URL } from "./API";

const AddVendor = ({ onBack, onVendorAdded, editVendor = null, isEditMode = false }) => {
  const [formData, setFormData] = useState({
    vendor_name: '',
    name: '',
    contact_number: '',
    email: '',
    gst_no: '',
    bank_name: '',
    bank_account_number: '',
    ifsc_code: '',
    pan_no: '',
    state: '',
    address: '',
    vendor_logo: null
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isEditMode && editVendor) {
      setFormData({
        vendor_name: editVendor.vendor_name || '',
        name: editVendor.name || '',
        contact_number: editVendor.contact_number || '',
        email: editVendor.email || '',
        gst_no: editVendor.gst_no || '',
        bank_name: editVendor.bank_name || '',
        bank_account_number: editVendor.bank_account_number || '',
        ifsc_code: editVendor.ifsc_code || '',
        pan_no: editVendor.pan_no || '',
        state: editVendor.state || '',
        address: editVendor.address || '',
        vendor_logo: editVendor.vendor_logo || null
      });
      
      if (editVendor.vendor_logo) {
        if (editVendor.vendor_logo.startsWith('data:image/')) {
          setLogoPreview(editVendor.vendor_logo);
        } else {
          const logoUrl = `${API_URL}/${editVendor.vendor_logo}`;
          setLogoPreview(logoUrl);
        }
      }
    }
  }, [isEditMode, editVendor]);

  const validateContactNumber = (value) => {
    if (value === '') return '';
    if (!/^\d+$/.test(value)) {
      return 'Contact number must contain only digits';
    }
    if (value.length !== 10) {
      return 'Contact number must be exactly 10 digits';
    }
    return '';
  };

  const validatePanNumber = (value) => {
    if (value === '') return '';
    if (value.length !== 10) {
      return 'PAN number must be exactly 10 characters';
    }
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
      return 'Invalid PAN format (e.g., ABCDE1234F)';
    }
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'contact_number') {
      const error = validateContactNumber(value);
      setErrors(prev => ({
        ...prev,
        contact_number: error
      }));
    } else if (name === 'pan_no') {
      const error = validatePanNumber(value.toUpperCase());
      setFormData(prev => ({
        ...prev,
        pan_no: value.toUpperCase()
      }));
      setErrors(prev => ({
        ...prev,
        pan_no: error
      }));
    } else if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return 'Please select a valid image file (JPEG, PNG, GIF, WEBP)';
    }
    
    if (file.size > 5 * 1024 * 1024) {
      return 'File size should be less than 5MB';
    }
    
    return null;
  };

  const processFile = (file) => {
    const error = validateFile(file);
    if (error) {
      setErrors(prev => ({
        ...prev,
        vendor_logo: error
      }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      
      setFormData(prev => ({
        ...prev,
        vendor_logo: base64String
      }));
      
      setLogoPreview(base64String);
    };
    reader.readAsDataURL(file);

    if (errors.vendor_logo) {
      setErrors(prev => ({
        ...prev,
        vendor_logo: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      vendor_logo: null
    }));
    setLogoPreview(null);
    
    const fileInput = document.getElementById('vendorLogo');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const createVendor = async (vendorData) => {
    try {
      const response = await fetch(`${API_URL}/vendors/create_vendor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(vendorData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText || `HTTP error! status: ${response.status}` };
        }
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
      
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error(`Cannot connect to server. Please check if the backend is running on ${API_URL}`);
      }
      throw error;
    }
  };

  const updateVendor = async (vendorId, vendorData) => {
    try {
      const response = await fetch(`${API_URL}/vendors/update_vendor/${vendorId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(vendorData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText || `HTTP error! status: ${response.status}` };
        }
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
      
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error(`Cannot connect to server. Please check if the backend is running on ${API_URL}`);
      }
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const contactError = validateContactNumber(formData.contact_number);
    const panError = validatePanNumber(formData.pan_no);
    
    if (contactError || panError) {
      setErrors({
        contact_number: contactError,
        pan_no: panError
      });
      
      return;
    }
    
    setIsLoading(true);
    setErrors({});

    try {
      const vendorData = {
        vendor_name: formData.vendor_name.trim(),
        name: formData.name.trim(),
        contact_number: formData.contact_number.trim(),
        email: formData.email.trim(),
        gst_no: formData.gst_no.trim(),
        bank_name: formData.bank_name.trim(),
        bank_account_number: formData.bank_account_number.trim(),
        ifsc_code: formData.ifsc_code.trim(),
        pan_no: formData.pan_no.trim(),
        state: formData.state.trim(),
        address: formData.address.trim(),
        vendor_logo: formData.vendor_logo
      };

      let result;
      let newVendor;

      if (isEditMode && editVendor) {
        result = await updateVendor(editVendor.vendor_id, vendorData);
        
        newVendor = {
          ...editVendor,
          vendor_name: formData.vendor_name.trim(),
          name: formData.name.trim(),
          contact_number: formData.contact_number.trim(),
          email: formData.email.trim(),
          gst_no: formData.gst_no.trim(),
          bank_name: formData.bank_name.trim(),
          bank_account_number: formData.bank_account_number.trim(),
          ifsc_code: formData.ifsc_code.trim(),
          pan_no: formData.pan_no.trim(),
          state: formData.state.trim(),
          address: formData.address.trim(),
          vendor_logo: result.data?.vendor_logo || formData.vendor_logo,
          updated_on: new Date().toISOString(),
          originalData: result
        };

        alert('Vendor updated successfully!');
      } else {
        result = await createVendor(vendorData);
        
        newVendor = {
          id: result.vendor_id || Date.now(),
          vendor_id: result.vendor_id || Date.now(),
          vendor_name: formData.vendor_name.trim(),
          name: formData.name.trim(),
          contact_number: formData.contact_number.trim(),
          email: formData.email.trim(),
          gst_no: formData.gst_no.trim(),
          bank_name: formData.bank_name.trim(),
          bank_account_number: formData.bank_account_number.trim(),
          ifsc_code: formData.ifsc_code.trim(),
          pan_no: formData.pan_no.trim(),
          state: formData.state.trim(),
          address: formData.address.trim(),
          vendor_logo: result.data?.vendor_logo || formData.vendor_logo,
          updated_on: new Date().toISOString(),
          originalData: result
        };

        alert('Vendor added successfully!');
      }

      if (onVendorAdded) {
        onVendorAdded(newVendor);
      }
      
      if (!isEditMode) {
        setFormData({
          vendor_name: '',
          name: '',
          contact_number: '',
          email: '',
          gst_no: '',
          bank_name: '',
          bank_account_number: '',
          ifsc_code: '',
          pan_no: '',
          state: '',
          address: '',
          vendor_logo: null
        });
        setLogoPreview(null);
        
        const fileInput = document.getElementById('vendorLogo');
        if (fileInput) {
          fileInput.value = '';
        }
      }

      if (onBack) {
        onBack();
      }

    } catch (error) {
      setErrors({
        submit: error.message || `Failed to ${isEditMode ? 'update' : 'create'} vendor. Please try again.`
      });
      
      alert('Error: ' + (error.message || `Failed to ${isEditMode ? 'update' : 'create'} vendor. Please try again.`));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  return (
    <div className="AV-00">
      <div className="AV-01">
        <div className="AV-02">
          <h1 className="AV-03">
            {isEditMode ? 'Edit Vendor' : 'Add New Vendor'}
          </h1>
        </div>
        
        <div className="AV-04">
          <div className="AV-05">
            {errors.submit && (
              <div className="AV-submit-error">
                {errors.submit}
              </div>
            )}

            <div className="AV-68">
              <div className="AV-12">
                <label className="AV-13" htmlFor="vendor_name">
                  Vendor Name
                </label>
                <input
                  type="text"
                  id="vendor_name"
                  name="vendor_name"
                  className="AV-15"
                  value={formData.vendor_name}
                  onChange={handleInputChange}
                  placeholder="Enter Vendor Name"
                  disabled={isLoading}
                />
              </div>

              <div className="AV-18">
                <label className="AV-19" htmlFor="name">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="AV-21"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter Name"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="AV-68">
              <div className="AV-24">
                <label className="AV-25" htmlFor="contact_number">
                  Contact Number
                </label>
                <input
                  type="text"
                  id="contact_number"
                  name="contact_number"
                  className="AV-27"
                  value={formData.contact_number}
                  onChange={handleInputChange}
                  placeholder="Enter Contact Number"
                  disabled={isLoading}
                />
                {errors.contact_number && (
                  <span className="AV-38">{errors.contact_number}</span>
                )}
              </div>

              <div className="AV-62">
                <label className="AV-63" htmlFor="email">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="AV-64"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter Email"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="AV-68">
              <div className="AV-65">
                <label className="AV-66" htmlFor="gst_no">
                  GST No
                </label>
                <input
                  type="text"
                  id="gst_no"
                  name="gst_no"
                  className="AV-67"
                  value={formData.gst_no}
                  onChange={handleInputChange}
                  placeholder="Enter GST No"
                  disabled={isLoading}
                />
              </div>

              <div className="AV-69">
                <label className="AV-70" htmlFor="bank_name">
                  Bank Name
                </label>
                <input
                  type="text"
                  id="bank_name"
                  name="bank_name"
                  className="AV-71"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  placeholder="Enter Bank Name"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="AV-68">
              <div className="AV-72">
                <label className="AV-73" htmlFor="bank_account_number">
                  Bank Account Number
                </label>
                <input
                  type="text"
                  id="bank_account_number"
                  name="bank_account_number"
                  className="AV-74"
                  value={formData.bank_account_number}
                  onChange={handleInputChange}
                  placeholder="Enter Bank Account Number"
                  disabled={isLoading}
                />
              </div>

              <div className="AV-75">
                <label className="AV-76" htmlFor="ifsc_code">
                  IFSC Code
                </label>
                <input
                  type="text"
                  id="ifsc_code"
                  name="ifsc_code"
                  className="AV-77"
                  value={formData.ifsc_code}
                  onChange={handleInputChange}
                  placeholder="Enter IFSC Code"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="AV-68">
              <div className="AV-72">
                <label className="AV-73" htmlFor="pan_no">
                  PAN No
                </label>
                <input
                  type="text"
                  id="pan_no"
                  name="pan_no"
                  className="AV-74"
                  value={formData.pan_no}
                  onChange={handleInputChange}
                  placeholder="Enter PAN No"
                  disabled={isLoading}
                  maxLength="10"
                />
                {errors.pan_no && (
                  <span className="AV-38">{errors.pan_no}</span>
                )}
              </div>

              <div className="AV-75">
                <label className="AV-76" htmlFor="state">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  className="AV-77"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Enter State"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="AV-78">
              <label className="AV-79" htmlFor="address">
                Address
              </label>
              <textarea
                id="address"
                name="address"
                className="AV-80"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter Address"
                rows="3"
                disabled={isLoading}
              />
            </div>

            <div className="AV-30">
              <label className="AV-31" htmlFor="vendorLogo">
                Vendor Logo
              </label>
              
              <div 
                className={`AV-42 ${isDragOver ? 'AV-43' : ''} ${errors.vendor_logo ? 'AV-44' : ''} ${isLoading ? 'AV-disabled' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => !isLoading && document.getElementById('vendorLogo').click()}
              >
                <input
                  type="file"
                  id="vendorLogo"
                  name="vendorLogo"
                  className="AV-58"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isLoading}
                />
                
                {!logoPreview ? (
                  <div className="AV-45">
                    <div className="AV-46">
                      <svg className="AV-47" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                      </svg>
                    </div>
                    <div className="AV-48">
                      <span className="AV-49">Click to upload</span> or drag and drop
                    </div>
                    <div className="AV-50">
                      PNG, JPG, GIF up to 5MB
                    </div>
                  </div>
                ) : (
                  <div className="AV-51">
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      className="AV-52"
                    />
                    <div className="AV-53">
                      <button 
                        type="button" 
                        className="AV-54" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage();
                        }} 
                        title="Remove image"
                        disabled={isLoading}
                      >
                        <svg className="AV-55" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {errors.vendor_logo && <span className="AV-38">{errors.vendor_logo}</span>}
            </div>
          </div>

          <div className="AV-39">
            <button 
              type="button" 
              className="AV-40" 
              onClick={handleBack}
              disabled={isLoading}
            >
              Back
            </button>
            <button 
              type="button" 
              className="AV-41" 
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading 
                ? (isEditMode ? 'Updating...' : 'Creating...') 
                : (isEditMode ? 'Update' : 'Submit')
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVendor;